import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import SectionTitle from '../UI/SectionTitle';
import ProductCard from './ProductCard';
import Button, { ButtonVariants } from '../UI/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faTimes } from '@fortawesome/free-solid-svg-icons';

// Импорт изображения
import stretchFilmImage from '../../assets/images/stretch_film.jpg';
import primaryFilmImage from '../../assets/images/photo_2025-04-21_22-39-53.jpg';
import secondaryFilmImage from '../../assets/images/photo_2025-04-21_22-47-00.jpg';
import miniFilmImage from '../../assets/images/photo_2025-04-21_22-49-09.jpg';

// Единое изображение для всех продуктов
const productImages = {
  transparent: stretchFilmImage,
  primary: primaryFilmImage,
  secondary: secondaryFilmImage,
  black: stretchFilmImage,
  reinforced: stretchFilmImage,
  mini: miniFilmImage,
  colorful: stretchFilmImage
};

// Демо-данные продуктов
const productsData = [
  {
    id: 1,
    name: 'Стрейч-пленка 1 кг, первичное сырье',
    description: 'Стандартная прозрачная стрейч-пленка из первичного сырья, 1 кг',
    price: '310.00 ₽',
    wholesalePrice: '263.50 ₽ / опт',
    imageUrl: primaryFilmImage,
    type: 'transparent',
    thickness: '17mkm',
    purpose: 'light',
    volume: 'retail',
    inStock: true,
    rating: 5,
    reviews: 12
  },
  {
    id: 2,
    name: 'Стрейч-пленка 2 кг, первичное сырье',
    description: 'Прозрачная стрейч-пленка из первичного сырья, 2 кг',
    price: '550.00 ₽',
    wholesalePrice: '467.50 ₽ / опт',
    imageUrl: primaryFilmImage,
    type: 'transparent',
    thickness: '20mkm',
    purpose: 'medium',
    volume: 'retail',
    inStock: true,
    rating: 5,
    reviews: 15
  },
  {
    id: 3,
    name: 'Стрейч-пленка 1 кг, вторичное сырье',
    description: 'Стрейч-пленка из вторичного сырья, 1 кг',
    price: '275.00 ₽',
    oldPrice: '305.00 ₽',
    discount: '-10%',
    wholesalePrice: '235.00 ₽ / опт',
    imageUrl: productImages.secondary,
    type: 'transparent',
    thickness: '17mkm',
    purpose: 'light',
    volume: 'retail',
    inStock: true,
    rating: 4,
    reviews: 8
  },
  {
    id: 4,
    name: 'Стрейч-пленка 2 кг, вторичное сырье',
    description: 'Стрейч-пленка из вторичного сырья, 2 кг',
    price: '530.00 ₽',
    oldPrice: '610.00 ₽',
    discount: '-13%',
    wholesalePrice: '450.00 ₽ / опт',
    imageUrl: productImages.secondary,
    type: 'transparent',
    thickness: '20mkm',
    purpose: 'medium',
    volume: 'retail',
    inStock: true,
    rating: 5,
    reviews: 10
  },
  {
    id: 5,
    name: 'Стрейч-пленка микро ролик 125 мм, 0,5 кг',
    description: 'Компактный микро ролик стрейч-пленки, 125 мм, вес 0,5 кг',
    price: '180.00 ₽',
    oldPrice: '200.00 ₽',
    discount: '-10%',
    tag: 'Хит продаж',
    wholesalePrice: '153.00 ₽ / опт',
    imageUrl: productImages.mini,
    type: 'transparent',
    thickness: '17mkm',
    purpose: 'light',
    volume: 'retail',
    inStock: true,
    rating: 5,
    reviews: 7
  },
  {
    id: 6,
    name: 'Стрейч-пленка мини ролик 250 мм, 1 кг',
    description: 'Мини ролик стрейч-пленки шириной 250 мм, вес 1 кг',
    price: '330.00 ₽',
    wholesalePrice: '280.00 ₽ / опт',
    imageUrl: productImages.mini,
    type: 'transparent',
    thickness: '17mkm',
    purpose: 'light',
    volume: 'retail',
    inStock: true,
    rating: 4,
    reviews: 5
  },
  {
    id: 7,
    name: 'Стрейч-пленка белая микро ролик 125 мм, 0,5 кг',
    description: 'Белая стрейч-пленка, микро ролик 125 мм, вес 0,5 кг',
    price: '200.00 ₽',
    wholesalePrice: '170.00 ₽ / опт',
    imageUrl: productImages.mini,
    type: 'colorful',
    thickness: '17mkm',
    purpose: 'light',
    volume: 'retail',
    inStock: true,
    rating: 4,
    reviews: 3
  },
  {
    id: 8,
    name: 'Стрейч-пленка белая, мини ролик 250 мм, 1 кг',
    description: 'Белая стрейч-пленка, мини ролик шириной 250 мм, вес 1 кг',
    price: '360.00 ₽',
    wholesalePrice: '306.00 ₽ / опт',
    imageUrl: productImages.mini,
    type: 'colorful',
    thickness: '17mkm',
    purpose: 'light',
    volume: 'retail',
    inStock: true,
    rating: 5,
    reviews: 11
  }
];

const CatalogSection = styled.section`
  padding: var(--spacing-xxl) 0;
`;

const CatalogContainer = styled.div`
  max-width: var(--container-width);
  padding: 0 var(--container-padding);
  margin: 0 auto;
`;

const FiltersContainer = styled.div`
  margin-bottom: var(--spacing-xl);
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (min-width: 768px) {
    align-items: flex-start;
  }
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: var(--spacing-md);
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FilterLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: var(--spacing-xs);
  color: var(--color-text-light);
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border-radius: var(--border-radius-md);
  border: 1px solid var(--color-border);
  background-color: white;
  font-family: var(--font-main);
  min-width: 200px;
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const MobileFilterButton = styled(Button)`
  display: none;
  align-items: center;
  gap: 8px;
  margin-bottom: var(--spacing-lg);
  
  @media (max-width: 768px) {
    display: flex;
    margin: 0 auto;
  }
`;

const MobileFiltersOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-md);
`;

const MobileFiltersPanel = styled(motion.div)`
  background-color: white;
  border-radius: var(--border-radius-lg);
  width: 100%;
  max-width: 500px;
  padding: var(--spacing-lg);
  max-height: 80vh;
  overflow-y: auto;
`;

const MobileFiltersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-lg);
`;

const MobileFiltersTitle = styled.h3`
  margin: 0;
  font-size: 1.2rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.2rem;
  color: var(--color-text);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 30px;
  margin-bottom: var(--spacing-xl);
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 25px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 20px;
    justify-items: center;
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    justify-items: center;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: var(--spacing-xxl) 0;
  grid-column: 1 / -1;
`;

const NoResultsTitle = styled.h3`
  color: var(--color-text);
  margin-bottom: var(--spacing-md);
`;

const NoResultsText = styled.p`
  color: var(--color-text-light);
  margin-bottom: var(--spacing-lg);
`;

const ApplyFiltersButton = styled(Button)`
  margin-top: var(--spacing-md);
`;

const ActiveFiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-lg);
`;

const ActiveFilterTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: var(--color-primary);
  color: white;
  padding: 4px 10px;
  border-radius: var(--border-radius-sm);
  font-size: 0.85rem;
  
  svg {
    cursor: pointer;
    font-size: 0.8rem;
  }
`;

const ClearFiltersButton = styled.button`
  background: none;
  border: none;
  color: var(--color-primary);
  font-size: 0.85rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  margin-left: var(--spacing-sm);
  
  &:hover {
    text-decoration: underline;
  }
`;

const LoadMoreButton = styled(Button)`
  margin: var(--spacing-xl) auto 0;
  display: flex;
  font-weight: 500;
  padding: 12px 30px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const Catalog = () => {
  const [filters, setFilters] = useState({
    type: '',
    thickness: '',
    purpose: '',
    volume: ''
  });
  
  const [filteredProducts, setFilteredProducts] = useState(productsData);
  const [visibleProducts, setVisibleProducts] = useState(6);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState(filters);
  
  // Применение фильтров
  useEffect(() => {
    const newFilteredProducts = productsData.filter(product => {
      let matchesType = !filters.type || product.type === filters.type;
      let matchesThickness = !filters.thickness || product.thickness === filters.thickness;
      let matchesPurpose = !filters.purpose || product.purpose === filters.purpose;
      let matchesVolume = !filters.volume || product.volume === filters.volume;
      
      return matchesType && matchesThickness && matchesPurpose && matchesVolume;
    });
    
    setFilteredProducts(newFilteredProducts);
    setVisibleProducts(6); // Сбрасываем количество отображаемых продуктов при изменении фильтров
  }, [filters]);
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };
  
  const handleMobileFilterChange = (e) => {
    const { name, value } = e.target;
    setTempFilters({
      ...tempFilters,
      [name]: value
    });
  };
  
  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setIsMobileFiltersOpen(false);
  };
  
  const handleRemoveFilter = (filterName) => {
    setFilters({
      ...filters,
      [filterName]: ''
    });
  };
  
  const handleClearAllFilters = () => {
    setFilters({
      type: '',
      thickness: '',
      purpose: '',
      volume: ''
    });
  };
  
  const toggleMobileFilters = () => {
    setIsMobileFiltersOpen(!isMobileFiltersOpen);
    setTempFilters(filters);
  };
  
  const handleLoadMore = () => {
    setVisibleProducts(prev => Math.min(prev + 6, filteredProducts.length));
  };
  
  const handleOrderProduct = (orderDetails) => {
    // В реальном проекте здесь будет логика отправки заказа на сервер
    console.log('Заказ оформлен:', orderDetails);
    
    // Можно было бы показать всплывающее уведомление через toast-библиотеку
    const message = `Заказ оформлен! Товар: ${orderDetails.name}, количество: ${orderDetails.quantity}, 
    сумма: ${orderDetails.totalPrice} руб. Данные для связи: ${orderDetails.customerInfo.phone}`;
    console.log(message);
  };
  
  // Получаем названия активных фильтров для отображения
  const getFilterLabel = (name, value) => {
    switch (name) {
      case 'type':
        if (value === 'transparent') return 'Прозрачная';
        if (value === 'black') return 'Черная';
        if (value === 'colorful') return 'Цветная';
        return value;
      case 'thickness':
        return value === '17mkm' ? '17 мкм' : value === '20mkm' ? '20 мкм' : '23 мкм';
      case 'purpose':
        return value === 'light' ? 'Для легких грузов' : value === 'medium' ? 'Для средних грузов' : 'Для тяжелых грузов';
      case 'volume':
        return value === 'retail' ? 'Розница' : 'Опт';
      default:
        return '';
    }
  };
  
  // Проверяем, есть ли активные фильтры
  const hasActiveFilters = Object.values(filters).some(value => value !== '');
  
  return (
    <CatalogSection id="catalog">
      <CatalogContainer>
        <SectionTitle
          title="Каталог стрейч-пленки"
          subtitle="Широкий выбор стрейч-пленки разной толщины и назначения для ваших грузов"
        />
        
        <FiltersContainer>
          <MobileFilterButton
            variant={'outlined'}
            onClick={toggleMobileFilters}
          >
            <FontAwesomeIcon icon={faFilter} />
            Фильтры
          </MobileFilterButton>
          
          <FiltersRow>
            <FilterGroup>
              <FilterLabel>Тип пленки</FilterLabel>
              <FilterSelect
                name="type"
                value={filters.type}
                onChange={handleFilterChange}
              >
                <option value="">Все типы</option>
                <option value="transparent">Прозрачная</option>
                <option value="black">Черная</option>
                <option value="colorful">Цветная</option>
              </FilterSelect>
            </FilterGroup>
            
            <FilterGroup>
              <FilterLabel>Толщина</FilterLabel>
              <FilterSelect
                name="thickness"
                value={filters.thickness}
                onChange={handleFilterChange}
              >
                <option value="">Любая толщина</option>
                <option value="17mkm">17 мкм</option>
                <option value="20mkm">20 мкм</option>
                <option value="23mkm">23 мкм</option>
              </FilterSelect>
            </FilterGroup>
            
            <FilterGroup>
              <FilterLabel>Назначение</FilterLabel>
              <FilterSelect
                name="purpose"
                value={filters.purpose}
                onChange={handleFilterChange}
              >
                <option value="">Любое назначение</option>
                <option value="light">Для легких грузов</option>
                <option value="medium">Для средних грузов</option>
                <option value="heavy">Для тяжелых грузов</option>
              </FilterSelect>
            </FilterGroup>
            
            <FilterGroup>
              <FilterLabel>Объем</FilterLabel>
              <FilterSelect
                name="volume"
                value={filters.volume}
                onChange={handleFilterChange}
              >
                <option value="">Любой объем</option>
                <option value="retail">Розница</option>
                <option value="wholesale">Опт</option>
              </FilterSelect>
            </FilterGroup>
          </FiltersRow>
          
          {hasActiveFilters && (
            <ActiveFiltersContainer>
              {Object.entries(filters).map(([key, value]) => (
                value && (
                  <ActiveFilterTag key={key}>
                    {getFilterLabel(key, value)}
                    <FontAwesomeIcon 
                      icon={faTimes} 
                      onClick={() => handleRemoveFilter(key)} 
                    />
                  </ActiveFilterTag>
                )
              ))}
              {hasActiveFilters && (
                <ClearFiltersButton onClick={handleClearAllFilters}>
                  Сбросить все
                </ClearFiltersButton>
              )}
            </ActiveFiltersContainer>
          )}
          
          <AnimatePresence>
            {isMobileFiltersOpen && (
              <MobileFiltersOverlay
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={toggleMobileFilters}
              >
                <MobileFiltersPanel
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MobileFiltersHeader>
                    <MobileFiltersTitle>Фильтры</MobileFiltersTitle>
                    <CloseButton onClick={toggleMobileFilters}>
                      <FontAwesomeIcon icon={faTimes} />
                    </CloseButton>
                  </MobileFiltersHeader>
                  
                  <FilterGroup>
                    <FilterLabel>Тип пленки</FilterLabel>
                    <FilterSelect
                      name="type"
                      value={tempFilters.type}
                      onChange={handleMobileFilterChange}
                    >
                      <option value="">Все типы</option>
                      <option value="transparent">Прозрачная</option>
                      <option value="black">Черная</option>
                      <option value="colorful">Цветная</option>
                    </FilterSelect>
                  </FilterGroup>
                  
                  <FilterGroup>
                    <FilterLabel>Толщина</FilterLabel>
                    <FilterSelect
                      name="thickness"
                      value={tempFilters.thickness}
                      onChange={handleMobileFilterChange}
                    >
                      <option value="">Любая толщина</option>
                      <option value="17mkm">17 мкм</option>
                      <option value="20mkm">20 мкм</option>
                      <option value="23mkm">23 мкм</option>
                    </FilterSelect>
                  </FilterGroup>
                  
                  <FilterGroup>
                    <FilterLabel>Назначение</FilterLabel>
                    <FilterSelect
                      name="purpose"
                      value={tempFilters.purpose}
                      onChange={handleMobileFilterChange}
                    >
                      <option value="">Любое назначение</option>
                      <option value="light">Для легких грузов</option>
                      <option value="medium">Для средних грузов</option>
                      <option value="heavy">Для тяжелых грузов</option>
                    </FilterSelect>
                  </FilterGroup>
                  
                  <FilterGroup>
                    <FilterLabel>Объем</FilterLabel>
                    <FilterSelect
                      name="volume"
                      value={tempFilters.volume}
                      onChange={handleMobileFilterChange}
                    >
                      <option value="">Любой объем</option>
                      <option value="retail">Розница</option>
                      <option value="wholesale">Опт</option>
                    </FilterSelect>
                  </FilterGroup>
                  
                  <ApplyFiltersButton
                    variant={'contained'}
                    fullWidth
                    onClick={handleApplyFilters}
                  >
                    Применить фильтры
                  </ApplyFiltersButton>
                </MobileFiltersPanel>
              </MobileFiltersOverlay>
            )}
          </AnimatePresence>
        </FiltersContainer>
        
        <ProductsGrid>
          {filteredProducts.length > 0 ? (
            filteredProducts.slice(0, visibleProducts).map((product, index) => (
              <ProductCard
                key={product.id}
                product={product}
                onOrder={handleOrderProduct}
                delay={index * 0.1}
              />
            ))
          ) : (
            <NoResults>
              <NoResultsTitle>Товары не найдены</NoResultsTitle>
              <NoResultsText>Попробуйте изменить параметры фильтрации</NoResultsText>
              <Button
                variant={'contained'}
                onClick={handleClearAllFilters}
              >
                Сбросить все фильтры
              </Button>
            </NoResults>
          )}
        </ProductsGrid>
        
        {filteredProducts.length > visibleProducts && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <LoadMoreButton
              variant={'outlined'}
              onClick={handleLoadMore}
            >
              Показать еще
            </LoadMoreButton>
          </motion.div>
        )}
      </CatalogContainer>
    </CatalogSection>
  );
};

export default Catalog; 